import React from 'react';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { useService } from '@xstate/react';
import { useHistory } from 'react-router-dom';

import { updateAndReplaceURLSearchParams } from '@trqk/utils';

import { BaseDropdownInput } from '../..';
import { defaultFilterHeight, defaultFilterWidth } from '../constants';

interface DropdownPreFilterProps<TData extends Record<string, unknown>> {
    /**
     * First element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    current: PreFilterState<TData>;
    /**
     * The specific interpreted state machine for this dropdown, held in the preFilter state machine
     * within context.dropdownInputMachines
     */
    dropdownInputMachine: DropdownInputMachine;
    /**
     * The preFilterConfig for this filter
     */
    preFilterConfig: PreFilterConfig;
    /**
     * Second element of array that results from calling `@xstate/react`'s useMachine method with
     * a machine generated by getPreFilterMachine
     */
    send: SendPreFilterEvent<TData>;
}

/**
 * Renders a DropdownInput that allows the user to set the current state of a preFilterValue.
 * @param props - see {@link DropdownPreFilterProps}
 */
export function DropdownPreFilter<TData extends Record<string, unknown>>({
    current,
    dropdownInputMachine,
    preFilterConfig,
    send,
}: DropdownPreFilterProps<TData>): JSX.Element {
    const {
        id,
        emptyOption,
        filterValuePath,
        height = defaultFilterHeight,
        width = defaultFilterWidth,
        urlParamKey,
    } = preFilterConfig;

    const dropdownInputOptions =
        current.context.dropdownInputOptionsData?.[preFilterConfig.id] ?? [];

    const history = useHistory();

    const deleteFilter = () => {
        // Send the event that updates the preFilterValue
        send({
            type: 'DELETE_PRE_FILTER_VALUES',
            value: {
                filterIds: [preFilterConfig.id],
            },
        });

        // Delete the corresponding URL parameter
        updateAndReplaceURLSearchParams(history, [
            {
                key: urlParamKey,
                value: null,
            },
        ]);
    };

    const setFilter = (preFilterValue: PreFilterValue): void => {
        // Send the event that updates the preFilterValue
        send({
            type: 'SET_PRE_FILTER_VALUES',
            value: {
                preFilterValues: [preFilterValue],
            },
        });

        // Set the corresponding URL parameter
        const filterValue = _get(preFilterValue, filterValuePath) ?? null;

        updateAndReplaceURLSearchParams(history, [
            {
                key: urlParamKey,
                value: filterValue,
            },
        ]);
    };

    const dropdownMachineData = useService(dropdownInputMachine);

    return (
        <BaseDropdownInput
            current={dropdownMachineData[0]}
            send={dropdownMachineData[1]}
            defaultId={emptyOption?.id ?? null}
            elementId={`pre-filter-${id}`}
            handleChange={(
                prevOption: DropdownInputOption | null,
                option: DropdownInputOption | null,
            ): void => {
                const value = _get(option, filterValuePath);

                if ([undefined, null].includes(value)) {
                    deleteFilter();
                } else {
                    const filterValue = {
                        id,
                        value: null,
                    };

                    _set(filterValue, filterValuePath, value);

                    setFilter(filterValue);
                }
            }}
            highlightWhenOpen={false}
            options={dropdownInputOptions}
            optionLists={[]}
            height={height}
            width={width}
        />
    );
}
